<template>
  <div class="pdf-viewer-container">
    <div class="pdf-viewer" ref="pdfViewer">
      <canvas
        ref="pdfCanvas"
        id="pdf-canvas"
        @contextmenu.prevent
        @dragstart.prevent
        @selectstart.prevent
      ></canvas>

      <!-- Loading overlay -->
      <div
        v-if="loading || loadingBrochures || (loadingPdf && !pdfUrl)"
        class="loading-overlay"
      >
        <div class="loading-content">
          <v-progress-circular
            :size="80"
            :width="6"
            color="primary"
            indeterminate
          ></v-progress-circular>
          <h3 class="mt-4 mb-2">{{ loadingTitle }}</h3>
          <p class="text-caption mb-0">{{ loadingSubtitle }}</p>
          <v-progress-linear
            v-if="(loadingPdf && !loadingBrochures) || loading"
            indeterminate
            color="primary"
            class="mt-3"
          ></v-progress-linear>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// Configure PDF.js worker - using CDN as fallback for compatibility
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default {
  name: "PdfViewer",
  props: {
    pdfUrl: {
      type: String,
      required: false,
    },
    scale: {
      type: Number,
      default: 1.5,
    },
    pageNumber: {
      type: Number,
      default: 1,
    },
    watermarkConfig: {
      type: Object,
      default: () => ({
        text: "CONFIDENTIAL",
        subtext: "Authorized Use Only",
        fontSize: 24,
        fontFamily: "Arial, sans-serif",
        color: "rgba(255, 0, 0, 0.2)",
        rotation: -45,
        spacing: 200,
      }),
    },
    userName: {
      type: String,
      default: "",
    },
    userFullName: {
      type: String,
      default: "",
    },
    loadingBrochures: {
      type: Boolean,
      default: false,
    },
    loadingPdf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pdfDoc: null,
      pageRendering: false,
      pageNumPending: null,
      loading: false,
      canvas: null,
      ctx: null,
    };
  },
  watch: {
    pdfUrl(newVal, oldVal) {
      console.log("PdfViewer: pdfUrl changed", { oldVal, newVal });
      if (newVal) {
        // Show loading immediately when new PDF URL is received
        this.showLoading();
        console.log("PdfViewer: Starting PDF load process");
        this.loadPDF();
      }
    },
    pageNumber(newPage) {
      if (this.pdfDoc) {
        this.queueRenderPage(newPage);
      }
    },
    scale() {
      if (this.pdfDoc) {
        this.queueRenderPage(this.pageNumber);
      }
    },
    loadingPdf(newVal) {
      console.log("PdfViewer: loadingPdf changed to", newVal);
    },
  },
  computed: {
    loadingTitle() {
      if (this.loadingBrochures) {
        return "Loading PDF Viewer";
      } else if (this.loadingPdf && !this.pdfUrl) {
        return "Loading PDF Viewer";
      } else if (this.loading) {
        return "Preparing Document";
      }
      return "Loading PDF...";
    },
    loadingSubtitle() {
      if (this.loadingBrochures) {
        return "Fetching available brochures...";
      } else if (this.loadingPdf && !this.pdfUrl) {
        return "Loading PDF document...";
      } else if (this.loading) {
        return "Processing PDF for secure viewing...";
      }
      return "Please wait while we prepare your document";
    },
  },
  mounted() {
    this.canvas = this.$refs.pdfCanvas;
    this.ctx = this.canvas.getContext("2d");
    this.initSecurityMeasures();

    if (this.pdfUrl) {
      this.loadPDF();
    }
  },
  methods: {
    async loadPDF() {
      try {
        console.log("PdfViewer: loadPDF called, URL:", this.pdfUrl);

        // Ensure loading is shown (might already be true from watcher)
        if (!this.loading) {
          this.showLoading();
        }

        console.log("PdfViewer: Starting PDF.js getDocument");
        const loadingTask = pdfjsLib.getDocument(this.pdfUrl);

        console.log("PdfViewer: Waiting for PDF document to load");
        this.pdfDoc = await loadingTask.promise;

        console.log(
          "PdfViewer: PDF document loaded, pages:",
          this.pdfDoc.numPages
        );
        this.$emit("pdf-loaded", {
          numPages: this.pdfDoc.numPages,
        });

        console.log("PdfViewer: Starting first page render");
        this.renderPage(this.pageNumber);

        console.log("PdfViewer: PDF loading complete, hiding loading overlay");
        this.hideLoading();
      } catch (error) {
        console.error("PdfViewer: Error loading PDF:", error);
        this.hideLoading();
        this.$emit("pdf-error", { error: error.message });
      }
    },

    async renderPage(pageNum) {
      if (pageNum < 1 || pageNum > this.pdfDoc.numPages) return;

      this.pageRendering = true;

      try {
        const page = await this.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: this.scale });

        this.canvas.height = viewport.height;
        this.canvas.width = viewport.width;

        const renderContext = {
          canvasContext: this.ctx,
          viewport: viewport,
        };

        await page.render(renderContext).promise;

        // Apply watermarks
        this.drawWatermark();
        //this.drawUserWatermark();

        this.$emit("page-rendered", { pageNumber: pageNum });

        this.pageRendering = false;
        if (this.pageNumPending !== null) {
          this.renderPage(this.pageNumPending);
          this.pageNumPending = null;
        }
      } catch (error) {
        console.error("Error rendering page:", error);
        this.pageRendering = false;
      }
    },

    queueRenderPage(num) {
      if (this.pageRendering) {
        this.pageNumPending = num;
      } else {
        this.renderPage(num);
      }
    },

    drawWatermark() {
      const config = this.watermarkConfig;
      const canvasWidth = this.canvas.width;
      const canvasHeight = this.canvas.height;

      if (!canvasWidth || !canvasHeight) return;

      this.ctx.save();

      const watermarkLines = [
        config.text,
        config.subtext,
        this.formatDateTime(),
      ];

      this.ctx.font = `${config.fontSize}px ${config.fontFamily}`;
      this.ctx.fillStyle = config.color;
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";

      const spacing = config.spacing;
      const lineHeight = config.fontSize + 10;

      for (let x = -spacing; x < canvasWidth + spacing; x += spacing) {
        for (let y = -spacing; y < canvasHeight + spacing; y += spacing) {
          this.ctx.save();
          this.ctx.translate(x, y);
          this.ctx.rotate((config.rotation * Math.PI) / 180);

          watermarkLines.forEach((line, index) => {
            const yOffset =
              (index - (watermarkLines.length - 1) / 2) * lineHeight;
            this.ctx.fillText(line, 0, yOffset);
          });

          this.ctx.restore();
        }
      }

      this.ctx.restore();
    },

    drawUserWatermark() {
      if (!this.userName && !this.userFullName) return;

      const canvasWidth = this.canvas.width;
      const canvasHeight = this.canvas.height;

      this.ctx.save();

      this.ctx.font = "60px Arial";
      this.ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
      this.ctx.textAlign = "center";

      const positions = [
        { x: canvasWidth * 0.25, y: canvasHeight * 0.25 },
        { x: canvasWidth * 0.5, y: canvasHeight * 0.5 },
        { x: canvasWidth * 0.75, y: canvasHeight * 0.75 },
      ];

      positions.forEach((pos) => {
        this.ctx.save();
        this.ctx.translate(pos.x, pos.y);
        this.ctx.rotate((-45 * Math.PI) / 180);

        if (this.userName) {
          this.ctx.fillText(this.userName, 0, -30);
        }
        if (this.userFullName) {
          this.ctx.fillText(this.userFullName, 0, 30);
        }

        this.ctx.restore();
      });

      this.ctx.restore();
    },

    formatDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    showLoading() {
      this.loading = true;
    },

    hideLoading() {
      this.loading = false;
    },

    fitToViewport() {
      if (!this.pdfDoc || !this.canvas) return;

      try {
        // Get the current page to calculate dimensions
        this.pdfDoc.getPage(this.pageNumber).then((page) => {
          // Get the PDF page dimensions at scale 1.0
          const viewport = page.getViewport({ scale: 1.0 });
          const pageWidth = viewport.width;
          const pageHeight = viewport.height;

          // Use window dimensions for more accurate viewport calculation
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;

          // Account for UI elements (TopNav, PdfControls, margins, etc.)
          const availableWidth = windowWidth - 80; // Account for sidebars and margins
          const availableHeight = windowHeight - 200; // Account for TopNav, controls, and margins

          // Calculate scale to fit the page height in the viewport
          // We prioritize fitting height, but also ensure width doesn't overflow
          const scaleToFitHeight = availableHeight / pageHeight;
          const scaleToFitWidth = availableWidth / pageWidth;

          // Use the smaller scale to ensure the page fits completely
          const optimalScale = Math.min(scaleToFitHeight, scaleToFitWidth);

          // Clamp the scale to reasonable bounds
          const clampedScale = Math.max(0.5, Math.min(3.0, optimalScale));

          console.log("Fit to viewport calculation:", {
            pageWidth,
            pageHeight,
            windowWidth,
            windowHeight,
            availableWidth,
            availableHeight,
            scaleToFitHeight,
            scaleToFitWidth,
            optimalScale,
            clampedScale,
          });

          // Emit the new scale back to parent
          this.$emit("scale-change", clampedScale);
        });
      } catch (error) {
        console.error("Error calculating fit-to-viewport scale:", error);
        // Fallback to default scale
        this.$emit("scale-change", 1.5);
      }
    },

    initSecurityMeasures() {
      // Prevent right-click
      this.canvas.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        return false;
      });

      // Prevent drag
      this.canvas.addEventListener("dragstart", (e) => {
        e.preventDefault();
        return false;
      });

      // Prevent selection
      this.canvas.addEventListener("selectstart", (e) => {
        e.preventDefault();
        return false;
      });

      // Override canvas export methods
      const originalToBlob = this.canvas.toBlob;
      const originalToDataURL = this.canvas.toDataURL;

      this.canvas.toBlob = function () {
        console.warn("Canvas export is disabled");
        return null;
      };

      this.canvas.toDataURL = function () {
        console.warn("Canvas export is disabled");
        return "";
      };

      // Cleanup on destroy
      this.$once("hook:beforeDestroy", () => {
        this.canvas.toBlob = originalToBlob;
        this.canvas.toDataURL = originalToDataURL;
      });
    },
  },
};
</script>

<style scoped>
/* Loading Overlay Styles */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

.loading-content h3 {
  color: #1976d2;
  margin-bottom: 1rem;
}

.loading-content p {
  color: #666;
  margin-bottom: 0.5rem;
}

.pdf-viewer-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.pdf-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #ecf0f1;
  overflow: auto;

  min-height: 500px;
}

#pdf-canvas {
  display: block;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  background: white;
  max-width: 100%;
  height: auto;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-content {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.loading-subtitle {
  font-size: 14px;
  color: #7f8c8d;
  line-height: 1.4;
}

/* Prevent printing */
@media print {
  * {
    display: none !important;
    visibility: hidden !important;
  }
}

/* Scrollbar styling */
.pdf-viewer::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.pdf-viewer::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.pdf-viewer::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 5px;
}

.pdf-viewer::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>